import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import {
  ModalOverlay,
  ModalContent,
  Input,
  Button,
  CloseButton,
  SearchResults,
  SearchResultItem,
  ProductDetailsSection,
  ProductDetailItem,
  LoadingRow,
  MessageRow,
} from "./SearchProductModalStyles";
import { fetchItem } from "../../network/Services";
import { useManufacturers } from "../../hooks/useManufacturers";
import { useProducts } from "../../hooks/useProducts";
import { getApiErrorMessage } from "../../utilities/apiErrors";
import { loadNextPageIfAvailable } from "../../hooks/infiniteListTypes";
import { createScrollHandler } from "../../utilities/infiniteScroll";

export enum SearchType {
  Manufacturer = "manufacturer",
  Product = "product",
}

interface Product {
  [product_ndc: string]: any;
  ndc: string;
  drugName: string;
  strength?: string;
  dosage?: string;
  package_size?: string | number;
  Controlled_Substance_Code?: string;
}

interface SearchProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: Product) => void;
  searchType: SearchType;
  selectedManufacturer?: string;
  onManufacturerSelect: (manufacturer: string) => void;
  loaderDispatch: any;
  setValue: (field: any, value: any) => void;
}

const SearchProductModal: React.FC<SearchProductModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  searchType,
  selectedManufacturer,
  onManufacturerSelect,
  loaderDispatch,
  setValue,
}) => {
  const [currentSearchType, setCurrentSearchType] = useState(searchType);
  const [manufacturerSearch, setManufacturerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [productDetails, setProductDetails] = useState<Product | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const isManufacturerStep = currentSearchType === SearchType.Manufacturer;

  const [manufacturers, , manufacturerState] = useManufacturers(
    manufacturerSearch,
    undefined,
    { enabled: isOpen && isManufacturerStep },
  );

  const [products, , productState] = useProducts(
    selectedManufacturer ?? "",
    productSearch,
    undefined,
    { enabled: isOpen && !isManufacturerStep },
  );

  useEffect(() => {
    if (isOpen) {
      setCurrentSearchType(searchType);
    }
  }, [isOpen, searchType]);

  useEffect(() => {
    if (!isOpen) {
      setManufacturerSearch("");
      setProductSearch("");
      setProductDetails(null);
      setDetailsError(null);
      setCurrentSearchType(searchType);
    }
  }, [isOpen, searchType]);

  const searchTerm = isManufacturerStep ? manufacturerSearch : productSearch;
  const setSearchTerm = isManufacturerStep
    ? setManufacturerSearch
    : setProductSearch;

  const listState = isManufacturerStep ? manufacturerState : productState;
  const isInitialLoading = listState.isFetching && !listState.isFetchingNextPage;
  const isLoadingMore = listState.isFetchingNextPage;
  const listError = listState.isError
    ? getApiErrorMessage(listState.error, "Failed to load search results.")
    : null;

  const results: Product[] = useMemo(() => {
    if (isManufacturerStep) {
      return (manufacturers ?? []).map((name) => ({
        ndc: "",
        drugName: name,
      }));
    }
    return (products ?? []).map((item) => ({
      ndc: item.ndc,
      drugName: item.drugName,
    }));
  }, [isManufacturerStep, manufacturers, products]);

  const listStateRef = React.useRef(listState);
  listStateRef.current = listState;

  const handleScroll = useMemo(
    () =>
      createScrollHandler(() => {
        loadNextPageIfAvailable(listStateRef.current);
      }),
    [],
  );

  const handleManufacturerSelect = (manufacturer: string) => {
    onManufacturerSelect(manufacturer);
    setProductSearch("");
    setProductDetails(null);
    setCurrentSearchType(SearchType.Product);
  };

  const handleSelect = async (item: Product) => {
    setDetailsError(null);
    setFetchingDetails(true);
    try {
      const details = await fetchItem(item.ndc, loaderDispatch);
      setProductDetails(details);
    } catch (error: unknown) {
      setProductDetails(null);
      setDetailsError(getApiErrorMessage(error, "Failed to fetch product details."));
    } finally {
      setFetchingDetails(false);
    }
  };

  const handleConfirmSelection = () => {
    if (productDetails) {
      onSelect(productDetails);
      setValue("ndc", productDetails.product_ndc);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <h2>
          Search {isManufacturerStep ? "Manufacturer" : "Product"}
        </h2>
        <Input
          type="text"
          placeholder={`Search ${currentSearchType}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <SearchResults onScroll={handleScroll}>
          {isInitialLoading ? (
            <LoadingRow>
              <CircularProgress size={24} />
            </LoadingRow>
          ) : null}

          {!isInitialLoading && listError ? (
            <MessageRow>
              {listError}
            </MessageRow>
          ) : null}

          {!isInitialLoading && !listError && results.length === 0 ? (
            <MessageRow>
              No Data Found
            </MessageRow>
          ) : null}

          {!isInitialLoading
        ? results.map((item, index) => (
            <SearchResultItem
              key={
                isManufacturerStep
                  ? `manufacturer-${index}-${item.drugName}`
                  : `product-${item.ndc || index}`
              }
              onClick={() =>
                isManufacturerStep
                  ? handleManufacturerSelect(item.drugName)
                  : handleSelect(item)
              }
            >
              {item.drugName}
            </SearchResultItem>
              ))
            : null}

          {isLoadingMore ? (
            <LoadingRow>
              <CircularProgress size={20} />
            </LoadingRow>
          ) : null}
        </SearchResults>

        {fetchingDetails ? <p>Fetching product details...</p> : null}
        {detailsError ? <p>{detailsError}</p> : null}
        {productDetails ? (
          <ProductDetailsSection>
            <h3>Product Details</h3>
            {productDetails.product_ndc ? (
              <ProductDetailItem>
                <strong>NDC:</strong> {productDetails.product_ndc}
              </ProductDetailItem>
            ) : null}
            {productDetails.strength ? (
              <ProductDetailItem>
                <strong>Strength:</strong> {productDetails.strength}
              </ProductDetailItem>
            ) : null}
            {productDetails.dosage ? (
              <ProductDetailItem>
                <strong>Dosage:</strong> {productDetails.dosage}
              </ProductDetailItem>
            ) : null}
            {productDetails.package_size ? (
              <ProductDetailItem>
                <strong>Package Size:</strong> {productDetails.package_size}
              </ProductDetailItem>
            ) : null}
            {productDetails.Controlled_Substance_Code ? (
              <ProductDetailItem>
                <strong>Controlled Substance Code:</strong>{" "}
                {productDetails.Controlled_Substance_Code}
              </ProductDetailItem>
            ) : null}
            <Button onClick={handleConfirmSelection}>Confirm</Button>
          </ProductDetailsSection>
        ) : null}
      </ModalContent>
    </ModalOverlay>
  );
};

export default SearchProductModal;
